import VueRouter from 'vue-router';
import {NotificationLike} from '@/types/NotificationLike.type';
import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {LogEntryObjectTypes} from '@/enums/LogEntryEnums';
import {silentRequest} from '@/helpers/featureProbe.helper';
import {getFocusLogId, scrollToEntryWhenRendered} from '@/components/timeline/timelineFocus.helper';
import {
    mentionPath,
    toDoIdOf,
    toNotificationView,
} from '@/components/app/appBar/notifications/notificationCenter/notificationView.helper';
import {resolveToDoPath} from '@/components/app/appBar/notifications/appNotifications/toDoPreview.helper';
import {TO_DO_ACTION_TYPES} from '@/helpers/notificationGroups.helper';
import {notificationPortal, portalNotificationPath} from '@/components/teacherPortal/portalNotificationPaths';

type Payload = Record<string, unknown>;

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** Hex id of an ObjectId, an id string or a populated document; undefined for anything else */
const idOf = (value: unknown): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }
    const raw = typeof value === 'object' && '_id' in (value as Payload) ? (value as Payload)._id : value;
    const id = String(raw);
    return OBJECT_ID.test(id) ? id : undefined;
};

const payloadOf = (notification: NotificationLike): Payload => {
    const payload = (notification as { messagePayload?: unknown }).messagePayload;
    return payload && typeof payload === 'object' ? payload as Payload : {};
};

const logEntryOf = (notification: NotificationLike): string | undefined =>
    idOf((notification as { logEntry?: unknown }).logEntry);

const isMentionNotification = (notification: NotificationLike): boolean => notification.type === NotificationTypes.MENTION;

/**
 * A responsible (contact person) has no own view: the profile of the first institution that lists them is opened.
 * The log entry belongs to the responsible, so no `?log=` (the institution timeline would not show it anyway).
 */
async function responsiblePath(responsibleId: string): Promise<string | undefined> {
    const result = await silentRequest<{ institutions?: Array<{ _id?: unknown }> }>('get', 'institutioner', '', {
        filter: {responsibles: [responsibleId]},
        fields: ['_id'],
        limit: 1,
    });
    const institutionId = result.ok ? idOf(result.data?.institutions?.[0]?._id) : undefined;
    return institutionId ? `/institutions/${institutionId}` : undefined;
}

/** Profile path of a mention payload (objectType/objectId), including responsibles */
async function pathFromPayload(payload: Payload, logEntryId?: string): Promise<string | undefined> {
    const direct = mentionPath(payload, logEntryId);
    if (direct) {
        return direct;
    }
    const objectId = idOf(payload.objectId);
    if (payload.objectType === LogEntryObjectTypes.RESPONSIBLE && objectId) {
        return responsiblePath(objectId);
    }
    return undefined;
}

/** The stored notification (the socket event might lack fields of older notifiers) */
async function fetchStoredNotification(id: string): Promise<NotificationLike | undefined> {
    const result = await silentRequest<{ notifications?: NotificationLike[] }>('get', 'notifier', 'own', {
        filter: {_id: [id]},
        limit: 1,
        skipPagination: true,
    });
    return result.ok ? result.data?.notifications?.[0] : undefined;
}

/** Object of the log entry itself, as last resort */
async function payloadFromLogEntry(logEntryId: string): Promise<Payload | undefined> {
    const result = await silentRequest<{ object?: unknown; objectType?: unknown }>('get', 'recorder', logEntryId, {
        fields: ['object', 'objectType'],
    });
    if (!result.ok || !result.data) {
        return undefined;
    }
    return {objectType: result.data.objectType, objectId: idOf(result.data.object)};
}

async function resolveMentionPath(notification: NotificationLike): Promise<string | undefined> {
    let logEntryId = logEntryOf(notification);
    const fromPayload = await pathFromPayload(payloadOf(notification), logEntryId);
    if (fromPayload) {
        return fromPayload;
    }
    const id = idOf(notification._id);
    const stored = id ? await fetchStoredNotification(id) : undefined;
    if (stored) {
        logEntryId = logEntryId || logEntryOf(stored);
        const fromStored = await pathFromPayload(payloadOf(stored), logEntryId);
        if (fromStored) {
            return fromStored;
        }
    }
    const fromLog = logEntryId ? await payloadFromLogEntry(logEntryId) : undefined;
    return fromLog ? pathFromPayload(fromLog, logEntryId) : undefined;
}

/** True when a click on the notification can open a target (mentions are resolved on click if needed) */
export function canOpenNotification(notification: NotificationLike): boolean {
    if (toNotificationView(notification).path) {
        return true;
    }
    // Portal: only targets with a portal page (mentions of office profiles have none)
    if (notificationPortal() || !isMentionNotification(notification)) {
        return false;
    }
    const payload = payloadOf(notification);
    return !!(logEntryOf(notification) || idOf(notification._id) || idOf(payload.objectId));
}

/**
 * Router path of the notification target. To-dos open the to-do (QM-Cockpit to-dos their case). Mentions without a direct path (responsible, incomplete socket payload)
 * are resolved via the stored notification or the log entry. Undefined when there is no target.
 */
export async function resolveNotificationPath(notification: NotificationLike): Promise<string | undefined> {
    return portalNotificationPath(await resolveOfficeNotificationPath(notification));
}

async function resolveOfficeNotificationPath(notification: NotificationLike): Promise<string | undefined> {
    const toDoId = TO_DO_ACTION_TYPES.includes(notification.type) ? toDoIdOf(notification) : undefined;
    if (toDoId) {
        // QM-Cockpit to-dos open their case (`?case=`), all others the to-do
        return resolveToDoPath(toDoId);
    }
    const path = toNotificationView(notification).path;
    if (path || !isMentionNotification(notification)) {
        return path;
    }
    try {
        return await resolveMentionPath(notification);
    } catch (e) {
        return undefined;
    }
}

export type NotificationOpenResult = 'opened' | 'redirected' | 'failed';

const { isNavigationFailure, NavigationFailureType } = VueRouter;

/**
 * Opens a path in the app. 'opened' only when the target route is shown afterwards; a guard redirect (e.g. no access)
 * gives 'redirected', an aborted or cancelled navigation or an error 'failed'. The same page again only scrolls to the
 * `?log=` entry (the router view is keyed by the full path, a different id or query remounts the view).
 */
const NAVIGATION_TIMEOUT_MS = 6000;

export async function openNotificationTarget(router: VueRouter, officePath: string): Promise<NotificationOpenResult> {
    const path = portalNotificationPath(officePath);
    if (!path) {
        return 'failed';
    }
    const target = router.resolve(path).route;
    if (router.currentRoute.fullPath === target.fullPath) {
        const logId = getFocusLogId(target);
        if (logId) {
            scrollToEntryWhenRendered(logId);
        }
        return 'opened';
    }
    try {
        // A navigation guard that never calls next() must not leave the pop-up blocked: give up waiting after a few
        // seconds and judge by where the router actually is
        await Promise.race([
            router.push(target.fullPath),
            new Promise((resolve) => setTimeout(resolve, NAVIGATION_TIMEOUT_MS)),
        ]);
    } catch (e) {
        if (isNavigationFailure(e, NavigationFailureType.redirected)) {
            return 'redirected';
        }
        if (!isNavigationFailure(e, NavigationFailureType.duplicated)) {
            return 'failed';
        }
    }
    return router.currentRoute.path === target.path ? 'opened' : 'failed';
}

/** Snackbar text when the target could not be opened; the notification stays unread */
export function openFailureText(result: NotificationOpenResult | 'missing'): string {
    switch (result) {
        case 'missing':
            return 'Zu dieser Benachrichtigung gibt es keine Seite mehr (z. B. gelöscht). Sie bleibt ungelesen.';
        case 'redirected':
            return 'Diese Seite ist für dich nicht freigegeben. Die Benachrichtigung bleibt ungelesen.';
        default:
            return 'Die Seite konnte nicht geöffnet werden. Die Benachrichtigung bleibt ungelesen.';
    }
}

/** Opens the target in a new browser tab (Ctrl/Cmd-click) */
export function openNotificationInNewTab(router: VueRouter, path: string): void {
    window.open(router.resolve(path).href, '_blank', 'noopener');
}
